import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { supabase } from "@/supabaseClient";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY after redirect from the reset email
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (session && event === "SIGNED_IN")) {
        setReady(true);
        setChecking(false);
      }
    });
    // Also check if a recovery session is already present
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
      setChecking(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated! Please sign in.");
      await supabase.auth.signOut();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.message || "Failed to update password");
    } finally { setSaving(false); }
  };

  const inputClass = "w-full h-11 rounded-xl text-sm pl-10 pr-12 bg-white/5 border border-white/10 text-white placeholder:text-zinc-600 outline-none focus:border-emerald-400/50 transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: "#111113", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-emerald-400/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Set New Password</h2>
          <p className="text-sm text-zinc-500 mt-1">Choose a new password for your account</p>
        </div>

        {checking ? (
          <p className="text-center text-sm text-zinc-500 py-6">Verifying reset link...</p>
        ) : ready ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
              <input data-testid="reset-new-password" type={showPw ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 6 characters)"
                className={inputClass} autoComplete="new-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white z-10 p-2 rounded-lg" tabIndex={-1}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button data-testid="reset-submit" type="submit" disabled={saving}
              className="w-full h-11 rounded-xl text-sm font-semibold bg-emerald-400 text-black hover:bg-emerald-300 transition-colors disabled:opacity-50">
              {saving ? "Updating..." : "Update Password"}
            </button>
          </form>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-zinc-500 mb-4">This reset link is invalid or has expired.</p>
            <button data-testid="back-to-home" onClick={() => navigate("/", { replace: true })}
              className="text-emerald-400 hover:text-emerald-300 text-sm font-medium">Back to Home</button>
          </div>
        )}
      </div>
    </div>
  );
}
